/**
 * Vocabulario del panel: como se dicen en castellano las cosas tecnicas.
 *
 * Un explorador de permisos que muestra `invoicing.invoice: can_write=true` no
 * sirve para decidir a quien darle que. Este modulo traduce codigos a lenguaje
 * del oficio. Es un diccionario, no datos: nada de lo que hay aca inventa
 * permisos ni registros; solo pone en castellano lo que el backend informa.
 */

import type { Domain } from '@/lib/types';
import type { CrudVerb, RoleCapability } from '@/panel/contract';
import { moduloDeModelo, verbosConcedidos } from '@/panel/contract';

// ---------------------------------------------------------------------------
// Verbos
// ---------------------------------------------------------------------------
export const ETIQUETA_VERBO: Record<CrudVerb, string> = {
  read: 'Ver',
  create: 'Crear',
  write: 'Modificar',
  delete: 'Eliminar',
};

/** Version larga para la ficha, donde hay lugar para una frase. */
export const ETIQUETA_VERBO_LARGA: Record<CrudVerb, string> = {
  read: 'consultar',
  create: 'dar de alta',
  write: 'editar',
  delete: 'eliminar',
};

/** Tono del chip por verbo: eliminar es el unico que se pinta como riesgo. */
export const TONO_VERBO: Record<CrudVerb, 'neutral' | 'accent' | 'warning' | 'danger'> = {
  read: 'neutral',
  create: 'accent',
  write: 'warning',
  delete: 'danger',
};

/**
 * Frase que resume lo que el rol puede hacer sobre un modelo.
 * Ejemplo: «dar de alta, editar y consultar Facturas de cliente».
 */
export function frasePermitida(capacidad: RoleCapability): string {
  const verbos = verbosConcedidos(capacidad).map((verbo) => ETIQUETA_VERBO_LARGA[verbo]);
  if (verbos.length === 0) return 'sin acceso';
  if (verbos.length === 1) return verbos[0] ?? '';
  const ultimo = verbos[verbos.length - 1] ?? '';
  return `${verbos.slice(0, -1).join(', ')} y ${ultimo}`;
}

// ---------------------------------------------------------------------------
// Areas
// ---------------------------------------------------------------------------
/**
 * Etiquetas de los modulos del ERP.
 *
 * Es un respaldo de traduccion, no una fuente de datos: solo se usa cuando el
 * endpoint no manda `area.label`. Los nombres son los de los `manifest.py`.
 */
const ETIQUETA_MODULO: Record<string, string> = {
  core: 'Núcleo del sistema',
  base: 'Organización y contactos',
  products: 'Catálogo',
  accounting: 'Contabilidad',
  crm: 'CRM',
  invoicing: 'Facturación',
  inventory: 'Depósito',
  services: 'Proyectos y servicios',
  hr: 'Recursos Humanos',
  sales: 'Ventas',
  purchase: 'Compras',
  bank: 'Bancos',
  l10n_ar: 'Localización fiscal argentina',
  // «Consorcios y administraciones» y no «Administración de consorcios», que es
  // como se llamaba: desde el arbol de tres niveles «Administración» es una
  // ENTIDAD del sistema (la firma), y el nombre viejo se leia como si este
  // modulo fuera esa ficha en vez del area entera.
  property_mgmt: 'Consorcios y administraciones',
  documents: 'Documentos',
  payroll_ar: 'Sueldos',
  compliance_ar: 'Cumplimiento normativo',
  studio: 'Personalización',
};

/**
 * Nombre visible de un area.
 *
 * @param clave clave del area (normalmente el modulo).
 * @param delBackend etiqueta que mando el backend, si mando alguna. Gana siempre.
 */
export function etiquetaDeArea(clave: string, delBackend?: string): string {
  if (delBackend !== undefined && delBackend !== '') return delBackend;
  return ETIQUETA_MODULO[clave] ?? clave;
}

/** Nombre visible de un modulo instalado, para la pantalla de estado. */
export function etiquetaDeModulo(nombre: string): string {
  return ETIQUETA_MODULO[nombre] ?? nombre;
}

// ---------------------------------------------------------------------------
// Alcance de registros (capa 2)
// ---------------------------------------------------------------------------
/**
 * Traduce un dominio de regla de registro a una frase.
 *
 * Cubre los marcadores dinamicos del backend (`$tenant.id`, `$company.ids`,
 * `$user.id`), que son los que aparecen en el 90% de las reglas. Cualquier otro
 * dominio devuelve `null` y la ficha muestra el dominio crudo: es preferible
 * mostrar el codigo a inventar una explicacion que no corresponde.
 */
export function frasearDominio(dominio: Domain | null): string | null {
  if (dominio === null || dominio.length === 0) return null;
  const texto = JSON.stringify(dominio);

  if (texto.includes('$user.id')) return 'Sólo los registros de la propia persona.';
  if (texto.includes('$company.ids')) return 'Sólo los de las empresas que tenga habilitadas.';
  if (texto.includes('$company.id')) return 'Sólo los de la empresa activa.';
  if (texto.includes('$tenant.id')) return 'Sólo los de su organización.';
  return null;
}

/** Frase final de un alcance: la del backend, la deducida, o el dominio crudo. */
export function textoDeAlcance(descripcion: string, dominio: Domain | null): { texto: string; crudo: boolean } {
  if (descripcion !== '') return { texto: descripcion, crudo: false };
  const deducida = frasearDominio(dominio);
  if (deducida !== null) return { texto: deducida, crudo: false };
  if (dominio !== null) return { texto: JSON.stringify(dominio), crudo: true };
  return { texto: 'Sin restricción de filas declarada.', crudo: false };
}

// ---------------------------------------------------------------------------
// Sinonimos para el buscador por capacidad
// ---------------------------------------------------------------------------
/**
 * Lo que la gente escribe -> palabras que aparecen en las etiquetas reales.
 *
 * El buscador NO fabrica resultados: expande el termino y despues busca contra
 * los modelos y areas que el endpoint devolvio. Si el sistema no tiene modulo de
 * facturacion, buscar "facturar" no devuelve nada, que es lo correcto.
 */
export const SINONIMOS: Record<string, string[]> = {
  facturar: ['factura', 'comprobante', 'invoicing', 'facturación'],
  factura: ['factura', 'comprobante', 'invoicing'],
  cobrar: ['cobro', 'pago', 'recibo', 'cuenta corriente'],
  cobranza: ['cobro', 'pago', 'recibo'],
  pagar: ['pago', 'orden de pago', 'proveedor'],
  vender: ['venta', 'pedido', 'presupuesto', 'sales'],
  presupuestar: ['presupuesto', 'pedido', 'venta'],
  comprar: ['compra', 'proveedor', 'orden'],
  contabilizar: ['asiento', 'contab', 'diario', 'cuenta'],
  contabilidad: ['asiento', 'contab', 'diario', 'cuenta', 'balance'],
  asiento: ['asiento', 'diario'],
  clientes: ['contacto', 'partner', 'cliente'],
  cliente: ['contacto', 'partner', 'cliente'],
  contactos: ['contacto', 'partner'],
  stock: ['stock', 'depósito', 'movimiento', 'inventory'],
  deposito: ['depósito', 'stock', 'ubicación'],
  empleados: ['empleado', 'contrato', 'ausencia', 'hr'],
  sueldos: ['empleado', 'contrato', 'liquidación'],
  // Acá había una entrada `expensas`. Se fue con la funcionalidad: el buscador
  // no fabrica resultados, pero expandía «expensas» a «unidad» y «consorcio» y
  // devolvía esas dos pantallas, o sea que le contestaba «sí» a quien preguntara
  // si el sistema liquida expensas. Sin la entrada, buscar «expensas» no
  // devuelve nada, que es la respuesta verdadera.
  consorcios: ['edificio', 'unidad', 'consorcio', 'property'],
  edificios: ['edificio', 'unidad', 'property'],
  proyectos: ['proyecto', 'tarea', 'parte de horas'],
  tickets: ['ticket', 'mesa de ayuda', 'sla'],
  oportunidades: ['lead', 'oportunidad', 'crm', 'embudo'],
  usuarios: ['usuario', 'grupo', 'permiso'],
  empresas: ['empresa', 'company', 'organización'],
  reportes: ['reporte', 'balance', 'informe'],
  productos: ['producto', 'artículo', 'catálogo', 'precio'],
  precios: ['precio', 'lista', 'producto'],
  impuestos: ['impuesto', 'iva', 'retención', 'percepción'],
  afip: ['iva', 'punto de venta', 'retención', 'l10n'],
  arca: ['iva', 'punto de venta', 'retención', 'l10n'],
};

/** Quita acentos y pasa a minusculas: buscar "facturacion" tiene que encontrar "facturación". */
export function normalizarTexto(texto: string): string {
  return texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');
}

/** Termino del usuario -> lista de terminos a buscar (el original mas sus sinonimos). */
export function expandirBusqueda(consulta: string): string[] {
  const base = normalizarTexto(consulta).trim();
  if (base === '') return [];
  const palabras = base.split(/\s+/).filter((palabra) => palabra.length >= 3);
  const terminos = new Set<string>(palabras.length > 0 ? palabras : [base]);

  for (const palabra of [...terminos]) {
    for (const [clave, valores] of Object.entries(SINONIMOS)) {
      const claveNormal = normalizarTexto(clave);
      // Prefijo en cualquiera de los dos sentidos: "factur" engancha "facturar".
      if (claveNormal.startsWith(palabra) || palabra.startsWith(claveNormal)) {
        for (const valor of valores) terminos.add(normalizarTexto(valor));
      }
    }
  }
  return [...terminos];
}

/** Nombre del modulo de un modelo, ya traducido. Atajo usado en varias pantallas. */
export function areaDeModelo(modelo: string): string {
  return etiquetaDeArea(moduloDeModelo(modelo));
}
